// ** React Imports
import {
  Fragment,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'

// ** MUI Imports
import { ChevronLeft, ExpandLess, ExpandMore } from '@mui/icons-material'
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  styled,
  useMediaQuery,
  useTheme
} from '@mui/material'
import MuiDrawer from '@mui/material/Drawer'

// **Component
import SwitchWorkspace from '../header/switch-workspace'

// ** Context Imports
import { SettingsContext } from '../../context/setting'

// **Constant Imports
import { sideBarItemsList } from './sidepanelItems'

// **Utils Imports
import {
  checkAppModeAccess,
  checkCandidateAccess,
  checkUserAccess,
  isRoleExcluded,
  showResumeFilteringStage
} from '../../utils/utils'

// ** Logo Imports
import DefaultLogo from '../../assets/images/logo-dark.svg'
import DefaultCombinedLogo from '../../assets/images/logo-with-wordmark-dark.svg'

// **Third party Imports
import { useSelector } from 'react-redux'

// ** SCSS Imports
import { APP_MODE } from '../../utils/constant'
import './index.scss'

// Functions
const isActivePath = (path) => {
  const currentPath = window.location.pathname?.toLowerCase()
  if (path === '/') {
    return currentPath === path
  }
  return (
    currentPath.startsWith(path) &&
    (currentPath[path.length] === '/' || currentPath[path.length] === undefined)
  )
}

// drawer width
const drawerWidth = 240

// Custom MUI styles
const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen
  }),
  overflowX: 'hidden'
})

const closedMixin = (theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 35px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 35px)`
  }
})

const Drawer = styled(MuiDrawer)(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  backgroundColor: theme.palette.primary.main,
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme)
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme)
  })
}))

// Seperate Image component
const ImageContainer = memo(({ mode, expanded }) => {
  const { contestUri } = useParams()
  const { settings } = useContext(SettingsContext)

  // variables
  const TenantCombinedLogo = useMemo(() => {
    return settings?.tenantConfig?.wordmark
  }, [settings?.tenantConfig?.wordmark])

  const TenantLogo = useMemo(() => {
    return settings?.tenantConfig?.logo
  }, [settings?.tenantConfig?.logo])
  return (
    <Box
      className="sidepanel-drawer-image-container"
      component={Link}
      to={contestUri ? `/c/${contestUri}/stages` : '/'}
      sx={{ '& img': { position: 'relative !important' } }}
    >
      {!expanded && (
        <img
          className="sidepanel-drawer-image-container-logo"
          src={TenantLogo || DefaultLogo}
          alt="logo"
        />
      )}

      {expanded && (
        <img
          className="sidepanel-drawer-image-container-wordmark"
          src={TenantCombinedLogo || DefaultCombinedLogo}
          alt="wordmark"
        />
      )}
    </Box>
  )
})

// Sidepanel components
const DrawerList = memo(
  ({ appMode, items = [], open, isMobileScreen, currentSubscription }) => {
    // hooks
    const navigate = useNavigate()

    const sideBarItemsWithIds = useMemo(
      () =>
        items?.map((item) => {
          const newItem = {
            ...item,
            id: item?.title?.toLowerCase().split(' ').join('-')
          }

          if (item?.subItems?.length > 0) {
            newItem.subItems = item.subItems.map((subItem) => ({
              ...subItem,
              id: subItem?.title?.toLowerCase().split(' ').join('-')
            }))
          }

          return newItem
        }),
      [items]
    )

    return (
      <List className="sidepanel-drawer-list">
        {sideBarItemsWithIds.map((item) => {
          // flag related variables
          const itemPlansExist = item?.plans
          const itemPlansIncludeSubscription =
            itemPlansExist && item.plans.includes(currentSubscription?.planName)
          const isRoleNotExcluded =
            item?.rolesExcluded && !isRoleExcluded(item?.rolesExcluded)
          const isRoleHasAccess =
            item?.roles?.length > 0 &&
            checkUserAccess(
              item.roles,
              item?.allowAdmin === undefined ? true : item?.allowAdmin
            )
          const itemHasAccess = isRoleNotExcluded || isRoleHasAccess
          const itemHasNoRolesDefined = !item?.rolesExcluded && !item?.roles
          const modeIncluded = checkAppModeAccess(
            item?.modeIncluded || [APP_MODE.HIRING],
            appMode
          )

          // conditional flag to render item
          const shouldRenderItem =
            modeIncluded &&
            (!itemPlansExist || itemPlansIncludeSubscription) &&
            (itemHasAccess || itemHasNoRolesDefined)

          return item?.subItems && shouldRenderItem ? (
            <SubItemList
              appMode={appMode}
              key={item?.path || item?.title}
              item={item}
              open={open}
              isMobileScreen={isMobileScreen}
              currentSubscription={currentSubscription}
            />
          ) : shouldRenderItem ? (
            <ListItem
              className={`sidepanel-drawer-list-item ${
                isActivePath(item?.path) ? 'active-list-item' : ''
              }`}
              key={item?.path || item?.title}
              disablePadding
            >
              <ListItemButton
                id={item?.id || ''}
                className="sidepanel-drawer-list-item-button"
                onClick={() => navigate(item.path)}
              >
                {!isMobileScreen && (
                  <>
                    <Box className="curve1" />
                    <Box className="curve2" />
                  </>
                )}
                {item?.icon && (
                  <ListItemIcon className="sidepanel-drawer-list-item-icon">
                    {item?.icon}
                  </ListItemIcon>
                )}
                <ListItemText
                  className="sidepanel-drawer-list-item-text"
                  sx={{
                    ...(open ? { opacity: 1 } : { opacity: 0 }),
                    marginTop: 0,
                    marginBottom: 0
                  }}
                  primary={item?.title}
                />
              </ListItemButton>
            </ListItem>
          ) : (
            <Fragment key={item?.path || item?.title}></Fragment>
          )
        })}
      </List>
    )
  }
)

// sidepanel subitem list component
const SubItemList = memo(
  ({
    open,
    item,
    appMode,
    isMobileScreen,
    currentSubscription,
    disablePadding = true
  }) => {
    // hooks
    const navigate = useNavigate()
    const location = useLocation()

    // states
    const [openList, setOpenList] = useState(false)

    const showProfileScreeningStage = useMemo(() => {
      return showResumeFilteringStage()
    }, [])

    const isParentActive = useMemo(() => {
      let isActive = false
      item?.subItems?.some((value) => {
        if (isActivePath(value?.path)) {
          isActive = true
          return true
        }
        return false
      })
      return isActive
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, location.pathname, item])

    // handler function
    const handleToggle = () => {
      setOpenList((prev) => !prev)
    }

    //useEffect
    useEffect(() => {
      if (isParentActive) {
        setOpenList(isParentActive)
      }
    }, [isParentActive])

    return (item?.roles?.length > 0 && checkUserAccess(item?.roles)) ||
      !item?.roles ? (
      <Fragment>
        <ListItem className={`subItem-item `} disablePadding={disablePadding}>
          <ListItemButton
            className="subItem-item-button"
            onClick={handleToggle}
          >
            {!isMobileScreen && (
              <>
                <Box className="curve1" />
                <Box className="curve2" />
              </>
            )}
            {item?.icon && (
              <ListItemIcon className="sidepanel-drawer-list-item-icon">
                {item.icon}
              </ListItemIcon>
            )}
            <ListItemText
              sx={{
                ...(open ? { opacity: 1 } : { opacity: 0 }),
                marginTop: 0,
                marginBottom: 0
              }}
              className="subItem-item-text"
              primary={item.title}
            />
            {open && (openList ? <ExpandLess /> : <ExpandMore />)}
          </ListItemButton>
        </ListItem>
        {openList && (
          <Box
            sx={{ width: '100%' }}
            className="sidepanel-subitem-collapse-container"
          >
            <List component="div" disablePadding className="subItem-list">
              {item.subItems.map((subItem) => {
                // flag variables
                const subItemPlansExist = subItem?.plans
                const subItemPlansIncludeSubscription =
                  subItemPlansExist &&
                  subItem.plans.includes(currentSubscription?.planName)
                const isRoleNotExcluded =
                  subItem?.rolesExcluded &&
                  !isRoleExcluded(subItem?.rolesExcluded)
                const isRoleHasAccess =
                  subItem?.roles?.length > 0 && checkUserAccess(subItem.roles)
                const subItemHasAccess = isRoleHasAccess || isRoleNotExcluded
                const modeIncluded = checkAppModeAccess(
                  subItem?.modeIncluded || [APP_MODE.HIRING],
                  appMode
                )

                const subItemHasNoRolesDefined =
                  !subItem?.rolesExcluded > 0 && !subItem?.roles

                if (
                  !showProfileScreeningStage &&
                  subItem?.id === 'resume-pools'
                ) {
                  return null
                }

                // flag to render item
                const shouldRenderItem =
                  modeIncluded &&
                  (!subItemPlansExist || subItemPlansIncludeSubscription) &&
                  (subItemHasAccess || subItemHasNoRolesDefined)

                return shouldRenderItem ? (
                  <ListItem
                    className={`subItem-item ${
                      isActivePath(subItem?.path) ? 'active-list-sub-item' : ''
                    }`}
                    key={subItem?.path || subItem?.title}
                    disablePadding
                  >
                    <ListItemButton
                      id={subItem?.id || ''}
                      className="subItem-item-button"
                      onClick={() => navigate(subItem?.path)}
                    >
                      {!isMobileScreen && (
                        <>
                          <Box className="curve1" />
                          <Box className="curve2" />
                        </>
                      )}
                      <ListItemIcon
                        className="subItem-icon"
                        sx={open && { pl: 2 }}
                      >
                        {subItem?.icon}
                      </ListItemIcon>
                      <ListItemText
                        sx={{
                          ...(open ? { opacity: 1 } : { opacity: 0 }),
                          marginTop: 0,
                          marginBottom: 0
                        }}
                        className="subItem-item-text"
                        primary={subItem?.title}
                      />
                    </ListItemButton>
                  </ListItem>
                ) : (
                  <Fragment key={subItem?.path || subItem?.key} />
                )
              })}
            </List>
          </Box>
        )}
      </Fragment>
    ) : (
      <Fragment></Fragment>
    )
  }
)

// hook

const SidePanel = () => {
  // hooks
  const theme = useTheme()
  const { settings, setSettings } = useContext(SettingsContext)
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('lg'))

  // Redux State
  const currentlyActivePlan = useSelector(
    (state) => state.auth.currentlyActivePlan
  )
  const role = useSelector((state) => state.auth.role)
  const appMode = useSelector((state) => state.auth.appMode)

  // context variables
  const { sidePanel, mode } = settings

  // variables
  const navbarHeight = 0

  //Handlers
  const handleDrawerExpandToggle = useCallback(() => {
    isMobileScreen
      ? setSettings((prev) => ({
          ...prev,
          sidePanel: { ...prev.sidePanel, opened: !prev.sidePanel.opened }
        }))
      : setSettings((prev) => ({
          ...prev,
          sidePanel: { ...prev.sidePanel, expanded: !prev.sidePanel.expanded }
        }))
  }, [setSettings, isMobileScreen])

  const setMainContainerWidth = useCallback((isPanelOpen, width = 240) => {
    const mainContainer = document.querySelector(
      '.private-route-main-container-wrapper'
    )
    if (mainContainer) {
      mainContainer.style.width = !isPanelOpen
        ? '100%'
        : `calc(100% - ${width || 0}px)`
    }
  }, [])

  useEffect(() => {
    if (isMobileScreen) {
      setMainContainerWidth(false)
    } else if (sidePanel?.hidden) {
      setMainContainerWidth(false)
    } else if (!sidePanel?.expanded) {
      setMainContainerWidth(true, 67)
    } else {
      setMainContainerWidth(true, 240)
    }
  }, [
    isMobileScreen,
    sidePanel?.expanded,
    sidePanel?.opened,
    sidePanel?.hidden,
    setMainContainerWidth
  ])

  return (
    !sidePanel?.hidden && (
      <Drawer
        PaperProps={{
          sx: !isMobileScreen ? { top: `${navbarHeight}px` } : {}
        }}
        className="sidepanel-drawer"
        variant={isMobileScreen ? 'temporary' : 'permanent'}
        open={isMobileScreen ? sidePanel?.opened : sidePanel?.expanded}
        onClose={handleDrawerExpandToggle}
        sx={{
          background: 'transparent',
          '.MuiPaper-root': {
            boxShadow: 'none'
          }
        }}
      >
        <Box
          className="sidepanel-drawer-expand-toggler-container"
          onClick={handleDrawerExpandToggle}
        >
          <ChevronLeft
            className={`sidepanel-drawer-expand-toggler-container-icon ${
              !sidePanel.expanded ? 'rotate' : ''
            }`}
          />
          {/* </IconButton> */}
        </Box>
        <ImageContainer mode={mode} expanded={sidePanel?.expanded} />

        {role ? (
          <DrawerList
            key={role}
            appMode={appMode}
            items={sideBarItemsList}
            open={sidePanel?.expanded}
            isMobileScreen={isMobileScreen}
            currentSubscription={currentlyActivePlan}
          />
        ) : (
          <Box sx={{ flex: 1 }} />
        )}

        <Box className="sidepanel-drawer-workspace-dropdown">
          {!checkCandidateAccess() && (
            <SwitchWorkspace
              drawerExpanded={sidePanel.expanded}
              currentlyActivePlan={currentlyActivePlan}
            />
          )}
        </Box>
      </Drawer>
    )
  )
}

export default memo(SidePanel)
